import { useEffect, useRef, useState, type TouchEvent } from 'react';
import { Link } from 'react-router-dom';
import { heroes } from '@/mocks/heroes';

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % heroes.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const go = (next: number) => setIndex((next + heroes.length) % heroes.length);

  const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  const current = heroes[index];

  return (
    <section className="relative w-full overflow-hidden rounded-lg bg-foreground-950">
      <div
        className="relative w-full h-[340px] md:h-[520px]"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {heroes.map((hero, i) => (
          <div
            key={hero.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <img
              src={hero.image}
              alt={`${hero.title} 主视觉`}
              title={`${hero.title} - 糖心视频`}
              className="w-full h-full object-cover object-top"
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-foreground-950/20"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-transparent to-transparent"></div>

        <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-12 max-w-2xl">
          <span className="inline-flex w-fit items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-semibold mb-4">
            <i className="ri-fire-fill"></i>
            {current.category} · 正在热播
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-background-50 leading-tight">
            {current.title}
          </h2>
          <p className="mt-3 text-background-100/90 text-sm md:text-base">{current.subtitle}</p>
          <p className="mt-2 text-background-200/70 text-xs md:text-sm">{current.meta}</p>
          <div className="flex items-center gap-3 mt-6">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-fill"></i>
              查看详情
            </Link>
            <Link
              to={`/category/${current.meta.includes('电影') ? 'movie' : current.category === '电视剧' ? 'tv' : current.category === '综艺' ? 'variety' : 'short'}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/15 text-background-50 text-sm font-semibold hover:bg-background-50/25 transition-colors whitespace-nowrap backdrop-blur cursor-pointer"
            >
              更多{current.category}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="absolute z-10 bottom-6 right-6 flex items-center gap-2">
          <button
            type="button"
            onClick={() => go(index - 1)}
            aria-label="上一张"
            className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-primary-500 transition-colors backdrop-blur cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            type="button"
            onClick={() => go(index + 1)}
            aria-label="下一张"
            className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-primary-500 transition-colors backdrop-blur cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>

        <div className="absolute z-10 bottom-7 left-6 md:left-12 flex items-center gap-2">
          {heroes.map((hero, i) => (
            <button
              key={hero.id}
              type="button"
              onClick={() => go(i)}
              aria-label={`切换到第 ${i + 1} 张`}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? 'w-8 bg-primary-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}